var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { collection, doc, getDoc, getDocs, onSnapshot, query, runTransaction, setDoc, where, } from 'firebase/firestore';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FirebaseAppContext, FirebaseAuthContext } from 'libs.firebase_react';
import { CodedError } from 'libs.localization.common';
import { Localization } from 'libs.localization.web';
export function useLocalizationCollection(assets) {
    const { db } = useContext(FirebaseAppContext);
    const { userClaims, loading: loadingAuth, error: errorAuth } = useContext(FirebaseAuthContext);
    const entityId = userClaims === null || userClaims === void 0 ? void 0 : userClaims.entityId;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const snapshotTime = useRef(Date.now());
    const { localizations, map, ids } = useMemo(() => {
        const map = new Map();
        const localizations = [];
        if (assets && Array.isArray(assets)) {
            for (const asset of assets) {
                try {
                    const localization = new Localization(asset);
                    map.set(asset, { localization });
                    localizations.push(localization);
                }
                catch (error) {
                    if (!(error instanceof CodedError && error.code === 'E_ASSET_NOT_SUPPORTED')) {
                        throw error;
                    }
                    console.error(error.message, asset);
                }
            }
        }
        const ids = localizations.map((localization) => localization.getId());
        return { localizations, map, ids };
    }, [assets]);
    const idString = useMemo(() => ids.sort().join(','), [ids]);
    const BATCH_SIZE = 30;
    useEffect(() => {
        if (!ids || ids.length === 0 || !entityId || !db) {
            setLoading(false);
            return;
        }
        const COLLECTION = `entities/${entityId}/localization`;
        const unsubscribeFns = [];
        let processedBatchesCount = 0;
        const totalBatches = Math.ceil(ids.length / BATCH_SIZE);
        const fetchData = () => __awaiter(this, void 0, void 0, function* () {
            setLoading(true);
            setError(null);
            try {
                for (let i = 0; i < ids.length; i += BATCH_SIZE) {
                    const batchValues = ids.slice(i, i + BATCH_SIZE);
                    const q = query(collection(db, COLLECTION), where('__name__', 'in', batchValues));
                    // Listen to real-time updates using onSnapshot
                    const unsubscribe = onSnapshot(q, (snapshot) => {
                        setData((prevData) => {
                            const updatedData = [...prevData, snapshot];
                            return updatedData;
                        });
                        // Increment count regardless of the number of documents in this snapshot
                        processedBatchesCount += 1;
                        // Check if all batches have been processed
                        if (processedBatchesCount >= totalBatches) {
                            setLoading(false);
                        }
                    }, (err) => {
                        setError(err);
                        setLoading(false); // Stop loading if there's an error
                    });
                    unsubscribeFns.push(unsubscribe);
                }
            }
            catch (err) {
                setError(err);
                setLoading(false); // Stop loading in case of an exception
            }
        });
        fetchData();
        // Clean up listeners on component unmount
        return () => {
            unsubscribeFns.forEach((unsubscribe) => unsubscribe());
        };
    }, [entityId, idString, db]);
    // Create a hash map of entries using the IDs as keys
    const entryMap = useMemo(() => {
        const map = new Map();
        if (error) {
            return map;
        }
        data.forEach((result) => result.docs.forEach((doc) => {
            map.set(doc.id, doc.data());
        }));
        snapshotTime.current = Date.now();
        return map;
    }, [data, error]);
    const localizationEntries = useMemo(() => {
        const entries = [];
        if (error) {
            return entries;
        }
        if (assets && Array.isArray(assets)) {
            for (const asset of assets) {
                const { localization } = map.get(asset) || {};
                const localizationEntry = entryMap.get(localization === null || localization === void 0 ? void 0 : localization.getId());
                if (db && localization && entityId) {
                    localization.configureLocalizationDatabase(doc, getDoc, setDoc, collection, query, where, getDocs, runTransaction, db, entityId);
                }
                entries.push(localizationEntry);
                map.set(asset, { localization, localizationEntry });
            }
        }
        return entries;
    }, [db, map, assets, entryMap, entityId]);
    return {
        isLoading: loadingAuth || loading,
        localizations,
        localizationsMap: map,
        localizationEntries,
        error: errorAuth || error,
        snapshotTime: snapshotTime.current,
    };
}
//# sourceMappingURL=localization_collection.hook.js.map